import type { ReactNode } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { cn } from '@/lib/utils';

import DataPointInfoButton from './DataPointInfoButton';

interface AnalyticsChartCardProps {
    title: string;
    description?: string;
    /** Optional glyph shown before the title. */
    icon?: ReactNode;
    /** Key into AnalyticsDocsContext; renders the (i) button when a doc exists. */
    docKey?: string;
    /** Extra header controls (tab switches, filters) rendered left of the (i). */
    actions?: ReactNode;
    className?: string;
    contentClassName?: string;
    children: ReactNode;
}

/**
 * The chrome every chart and stat section on the pre-defined analytics boards
 * renders in.
 *
 * The six board modules used to hand-roll this Card/CardHeader/CardTitle block
 * ~24 times over, which is why some carried a description and others did not.
 * Routing them through one wrapper also gives each card a single place to hang
 * its "(i)" data-point explanation.
 */
export function AnalyticsChartCard({
    title,
    description,
    icon,
    docKey,
    actions,
    className,
    contentClassName,
    children,
}: AnalyticsChartCardProps) {
    return (
        <Card className={className}>
            <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <div className="min-w-0 space-y-1.5">
                    <CardTitle className={icon ? 'inline-flex items-center gap-2' : undefined}>
                        {icon}
                        {title}
                    </CardTitle>
                    {description && <CardDescription>{description}</CardDescription>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    {actions}
                    <DataPointInfoButton docKey={docKey} />
                </div>
            </CardHeader>
            <CardContent className={cn(contentClassName)}>{children}</CardContent>
        </Card>
    );
}

export default AnalyticsChartCard;
